import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchProductById } from "../services/productApi";
import WhatsAppButton from "../components/WhatsAppButton";
import { ArrowLeft, Leaf, BookOpen } from "lucide-react";
import defaultImage from "../assets/logo.jpeg";
import { useTranslation } from "react-i18next";

const ProductDetails = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const data = await fetchProductById(id);
        setProduct(data);
      } catch (err) {
        if (err.response?.status === 404) {
          setNotFound(true);
        }
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id]);

  if (loading) {
    return (
      <div className="section-padding text-center">
        <p>{t("products.loading")}</p>
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="section-padding">
        <div className="container-custom text-center">
          <h1 className="text-3xl font-serif font-bold mb-4">
            {t("products.notFound")}
          </h1>
          <Link to="/products" className="btn-hero inline-flex">
            {t("products.back")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <div className="bg-secondary py-4">
        <div className="container-custom">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft size={18} />
            <span>{t("products.backToProducts")}</span>
          </Link>
        </div>
      </div>

      {/* Product Details */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image */}
            <div className="animate-fade-in">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-large">
                <img
                  src={product.image || defaultImage} // fallback to default
                  alt={lang === "ar" ? product.name_ar : product.name_en}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Content */}
            <div className="space-y-8 animate-slide-up">
              {/* Category */}
              <span className="inline-block px-4 py-1.5 text-sm font-medium bg-muted text-muted-foreground rounded-full">
                {lang === "ar"
                  ? product.category.name_ar
                  : product.category.name_en}
              </span>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-serif font-bold">
                {lang === "ar" ? product.name_ar : product.name_en}
              </h1>

              {/* Description */}
              <p className="text-lg text-muted-foreground leading-relaxed">
                {lang === "ar"
                  ? product.fullDescription_ar
                  : product.fullDescription_en}
              </p>

              {/* Price */}
              {product.price ? (
                <div className="text-4xl font-bold text-primary">
                  ${Number(product.price).toFixed(2)}
                </div>
              ) : (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Leaf className="w-6 h-6" />
                  <span className="text-lg italic">
                    {t("products.priceAvailable")}
                  </span>
                </div>
              )}

              {/* WhatsApp Order */}
              <div className="max-w-sm">
                <WhatsAppButton
                  variant="full"
                  productName={
                    lang === "ar" ? product.name_ar : product.name_en
                  }
                  productPrice={product.price || t("products.priceRequest")}
                />
              </div>

              {/* Info Cards */}
              <div className="space-y-6 pt-4">
                {/* Ingredients */}
                {product.ingredients !== "null" && (
                  <div className="p-6 bg-card rounded-2xl shadow-soft">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                        <Leaf className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="text-lg font-serif font-semibold">
                        {t("admin.products.ingredients")}
                      </h3>
                    </div>
                    <ul className="space-y-2">
                      <li className="text-muted-foreground flex items-start gap-2">
                        {lang === "ar"
                          ? product.ingredients_ar
                          : product.ingredients_en}
                      </li>
                    </ul>
                  </div>
                )}

                {/* Usage */}
                {product.usage !== "null" && (
                  <div className="p-6 bg-card rounded-2xl shadow-soft">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="text-lg font-serif font-semibold">
                        {t("admin.products.usage")}
                      </h3>
                    </div>
                    <p className="text-muted-foreground">
                      {lang === "ar" ? product.usage_ar : product.usage_en}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetails;
